import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { connect } from 'react-redux';
import { Autocomplete, CircularProgress, Drawer, TextField, Tooltip } from "@mui/material";
import axios from 'axios';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { styled } from '@mui/material/styles';
import Sidebar from "./components/sidebar";
import Header from "./components/header";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Modal from 'react-bootstrap/Modal';  
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
const mySwal = withReactContent(Swal);
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    flexGrow: 1,
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: `-20%`,
    ...(open && {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
      maxWidth: "80%"
    }),
  }),
);
const MechanicsManager = ({ role, token }) => {
  const navigate = useNavigate();

  const [openModal, setOpenModal] = useState(false);
  const [selectedHcp, setSelectedHcp] = useState(null);
  const [hcpList, setHcpList] = useState([]);
  const [ruleType, setRuleType] = useState("hospital");
  const [patientNric, setPatientNric] = useState("");
  const [patientList, setPatientList] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [brandList, setBrandList] = useState([]);
  const [selectedBrandId, setSelectedBrandId] = useState("");
  const [threshold, setThreshold] = useState("");
  const [redeemQuantity, setRedeemQuantity] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [errors, setErrors] = useState({});
  const [openSidebar, setOpenSidebar] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (role && token) {
      if (role === "hcp" || role === "sales") {
        mySwal.fire({ title: "Error", text: "Unauthorized Access", icon: "error", allowOutsideClick: false })
          .then(click => {
            if (click.isConfirmed) {
              navigate('/admin/login');
            }
          });
        return;
      }
      axios.post("/api/admin/getMechanicsThreshold", { role, token })
        .then(res => {
          setData(res.data.data);
          setLoading(false);
        })
        .catch(err => {
          setLoading(false);
          if (err.response?.data?.error === "Token Expired") {
            mySwal.fire({ title: "Error", text: "Token Expired", icon: "error", allowOutsideClick: false })
              .then(click => {
                if (click.isConfirmed) navigate('/admin/login');
              });
          } else {
            mySwal.fire("Error", err.response?.data?.error || "Internal Error", "error");
          }
        });
    } else {
      navigate('/admin/login');
    }
  }, [role, token, navigate]);

  // Fetch brand list for dropdown
  useEffect(() => {
  if (role && token) {
    axios.post("/api/admin/getBrands", { role, token })
      .then(res => setBrandList(res.data.data))
      .catch(err => mySwal.fire("Error", err.response?.data?.error || "Internal Error", "error"));
  }
  }, [role, token]);

  // Fetch HCP list for Autocomplete
  useEffect(() => {
    if (role && token) {
      axios.post("/api/admin/getAccountName", { token })
        .then(res => setHcpList(res.data.hcpData))
        .catch(err => mySwal.fire("Error", err.response?.data?.error || "Internal Error", "error"));
    }
  }, [role, token]);

  // Fetch patient NRIC list for Autocomplete
    useEffect(() => {
    if (role && token) {
      axios.post("/api/admin/getPatientNrics", { token })
        .then(res => setPatientList(res.data.patientData))
        .catch(err => mySwal.fire("Error", err.response?.data?.error || "Internal Error", "error"));
    }
  }, [role, token]);

const formatDate = (date) => {
  if (!date) return "-";

  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const statusConfig = {
  active:    { label: "ACTIVE",    bg: "#28A745" },
  scheduled: { label: "SCHEDULED", bg: "#FFC107" },
  expired:   { label: "EXPIRED",   bg: "#6C757D" },
  inactive:  { label: "INACTIVE",  bg: "#FF1E1E" },
};

const getStatusBadge = (item) => {
  const config = statusConfig[item.status] || statusConfig.inactive;
  return (
    <span style={{
      backgroundColor: config.bg,
      color: "#fff",
      padding: "8px 20px",
      borderRadius: "999px",
      fontSize: "12px",
      fontWeight: 600,
    }}>
      {config.label}
    </span>
  );
};

const inputStyle = {
  height: "52px",
  borderRadius: "8px",
  border: "2px solid #979797",
  fontSize: "15px",
};

const handleSubmit = () => {
  const newErrors = {};
  if (!editingItem) {
    if (ruleType === "hospital" && !selectedHcp) newErrors.hospitalName = "Hospital is required";
    if (ruleType === "patient" && !selectedPatient) newErrors.patientNric = "Patient NRIC is required";
  }
  if (!selectedBrandId) newErrors.selectedBrandId = "Brand is required";
  if (!threshold) newErrors.threshold = "Buy quantity is required";
  if (!redeemQuantity) newErrors.redeemQuantity = "Get quantity is required";
  if (selectedBrandId && threshold) {
      const selectedBrand = brandList.find(b => b.id === selectedBrandId);
    if (selectedBrand && Number(threshold) > Number(selectedBrand.threshold)) {
      newErrors.threshold = `Buy quantity cannot exceed brand default (${selectedBrand.threshold})`;
    }
  }
  if (!editingItem && selectedBrandId) {
    const selectedMedication = brandList.find(b => b.id === selectedBrandId)?.medication;
    if (selectedMedication) {
      const duplicate = data.some(item => {
        if (item.medication !== selectedMedication) return false;
        if (ruleType === "hospital") return item.hcpName === selectedHcp?.hcpName;
        return item.patientNric === patientNric;
      });
      if (duplicate) {
        newErrors.selectedBrandId = "A rule already exists for this combination";
      }
    }
  }
  setErrors(newErrors);
  if (Object.keys(newErrors).length > 0) return;
  setSubmitting(true);

  const payload = {
    role, token,
    brandId: selectedBrandId,
    threshold: parseFloat(threshold),
    redeemQuantity: parseFloat(redeemQuantity),
    startDate: startDate || null,
    endDate: endDate || null
  };
  if (editingItem) {
    payload.id = editingItem.id;
    payload.is_active = isActive;
  } else {
    if (ruleType === "hospital") payload.hcpName = selectedHcp.hcpName;
    if (ruleType === "patient") payload.patientNric = patientNric;
  }

  const apiUrl = editingItem
    ? "/api/admin/editMechanicsThreshold"
    : "/api/admin/addMechanicsThreshold";

  axios.post(apiUrl, payload)
    .then(res => {
      setOpenModal(false);
      setEditingItem(null);
      setIsActive(true);
      setSelectedHcp(null); setSelectedPatient(null); setPatientNric(""); setRuleType("hospital"); setSelectedBrandId(""); setThreshold("");
      setRedeemQuantity(""); setStartDate(null); setEndDate(null);
      axios.post("/api/admin/getMechanicsThreshold", { role, token })
        .then(r => setData(r.data.data));
      mySwal.fire("Success", "Mechanic rule saved successfully", "success");
    })
    .catch(err => {
      mySwal.fire("Error", err.response?.data?.error || "Internal Error", "error");
    })
    .finally(() => setSubmitting(false));
};

  const handleEdit = (item) => {
    setEditingItem(item);
    setRuleType(item.ruleType);
    setSelectedHcp(item.ruleType === 'hospital' ? hcpList.find(h => h.hcpName === item.hcpName) || null : null);
    setPatientNric(item.ruleType === 'patient' ? item.patientNric : '');
    setSelectedPatient(item.ruleType === 'patient' ? patientList.find(p => p.nric === item.patientNric) || null : null);
    setSelectedBrandId(
        brandList.find(b => b.medication === item.medication)?.id || ''
    );
    setThreshold(item.threshold);
    setRedeemQuantity(item.redeemQuantity);
    setIsActive(item.isActive);
    setStartDate(item.startDate ? new Date(item.startDate) : null);
    setEndDate(item.endDate ? new Date(item.endDate) : null);
    setOpenModal(true);
};
const handleDelete = (item) => {
    mySwal.fire({
        title: "Are you sure?",
        text: "This rule will be deactivated.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete"
    }).then(result => {
        if (result.isConfirmed) {
            axios.post("/api/admin/deleteMechanicsThreshold", { role, token, id: item.id })
                .then(() => {
                    axios.post("/api/admin/getMechanicsThreshold", { role, token })
                        .then(r => setData(r.data.data));
                    mySwal.fire("Deleted", "Mechanic rule deleted", "success");
                })
                .catch(err => mySwal.fire("Error", err.response?.data?.error || "Internal Error", "error"));
        }
    });
};

  return (
    <>
    <div className="d-flex">
      <Drawer
        sx={{
          minWidth: "20%",
          '& .MuiDrawer-paper': {
            minWidth: "20%",
            boxSizing: 'border-box',
            minHeight: "100vh",
            backgroundColor: "#FE7072",
          },
        }}
        anchor="left"
        open={openSidebar}
        variant="persistent">
        <Sidebar />
      </Drawer>
      <Main open={openSidebar}>
        <div className="mt-3 px-3">
          <Header title="MECHANICS CONFIGURATION" openSidebar={openSidebar} setOpenSidebar={setOpenSidebar} />
          <div className="mt-3">
            <p>Configure promotional rules for different hospitals and brands</p>

            <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "16px", color: "#555", fontSize: "14px" }}>
              <Tooltip
                title={
                  <span>
                    <strong>Priority order:</strong><br />
                    1. <strong>Patient (NRIC)</strong> : Highest priority. Applies to a specific patient & brand.<br />
                    2. <strong>HCP</strong> : Applies when no patient-specific rule exists.<br />
                    3. <strong>Brand default</strong> : Applies when no specific rule exists.
                  </span>
                }
                arrow
              >
                <InfoOutlinedIcon fontSize="small" sx={{ color: "#0093D1", cursor: "pointer" }} />
              </Tooltip>
              <span>Rules are applied by priority </span>
            </div>
            <div
  style={{
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: "24px",
  }}
>
  <button
  onClick={() => setOpenModal(true)}
    style={{
      backgroundColor: "#0D99D5",
      color: "#fff",
      border: "none",
      padding: "14px 24px",
      borderRadius: "12px",
      fontWeight: 600,
      cursor: "pointer",
    }}
  >
    + Add New Mechanic
  </button>
</div>
          </div>
          {loading ? (
            <div className="text-center"><CircularProgress /></div>
          ) : (
           <div
            style={{
              background: "#fff",
              borderRadius: "24px",
              overflow: "hidden",
              border: "1px solid #E5E5E5",
              boxShadow: "0 4px 10px rgba(0,0,0,0.08)",
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
              }}
            >
              <thead>
                <tr
                  style={{
                    backgroundColor: "#F8F8F8",
                    textAlign: "left",
                  }}
                >
                  <th style={{ padding: "24px" }}>User</th>
                  <th style={{ padding: "24px" }}>Type</th>
                  <th style={{ padding: "24px" }}>Brand</th>
                  <th style={{ padding: "24px" }}>Start Date</th>
                  <th style={{ padding: "24px" }}>End Date</th>
                  <th style={{ padding: "24px" }}>Mechanic Rule</th>
                  <th style={{ padding: "24px", textAlign: "center" }}>
                    Status
                  </th>
                  <th style={{ padding: "24px", textAlign: "center" }}>
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {data.map((item, index) => (
                  <tr
                    key={index}
                    style={{
                      borderTop: "1px solid #EAEAEA",
                    }}
                  >
                    <td style={{ padding: "24px" }}>
                      {item.ruleType === 'patient'
                        ? item.patientNric
                        : item.hcpName
                      }
                    </td>
                    <td style={{ padding: "24px" }}>
                      {item.ruleType === 'patient' ? 'Patient (NRIC)' : 'HCP'}
                    </td>
                    <td style={{ padding: "24px", fontWeight: 600 }}>
                      {item.medication}
                    </td>

                    <td style={{ padding: "24px" }}>
                      {formatDate(item.startDate)}
                    </td>

                    <td style={{ padding: "24px" }}>
                      {formatDate(item.endDate)}
                    </td>

                    <td style={{ padding: "24px" }}>
                      <div
                        style={{
                          color: "#0099FF",
                          fontWeight: 600,
                          lineHeight: "24px",
                        }}
                      >
                        <div>Buy {item.threshold}</div>
                        <div>Get {item.redeemQuantity}</div>
                      </div>
                    </td>

                    <td
                      style={{
                        padding: "24px",
                        textAlign: "center",
                      }}
                    >
                      {getStatusBadge(item)}
                    </td>

                    <td
                      style={{
                        padding: "24px",
                        textAlign: "center",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          gap: "10px",
                        }}
                      >
                        <button
                          style={{
                            backgroundColor: "#EAF7FF",
                            border: "none",
                            padding: "10px",
                            borderRadius: "8px",
                            cursor: "pointer",
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#d0f0ff")}
                          onClick={() => handleEdit(item)}
                        >
                          <EditOutlinedIcon color="primary" />
                        </button>

                        <button
                          style={{
                            backgroundColor: "#FFEBEE",
                            border: "none",
                            padding: "10px",
                            borderRadius: "8px",
                            cursor: "pointer",
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#ffcdd2")}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#FFEBEE")}
                          onClick={() => handleDelete(item)}
                        >
                          <DeleteOutlineOutlinedIcon color="error" /> 
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>
      </Main>
    </div>


    <style>
      {`
        .modal-full-rounded .modal-content { border-radius: 30px !important; }
        .modal-backdrop { z-index: 1300 !important; }
        .modal { z-index: 1301 !important; }
        .modal-full-rounded .modal-header {border-top-left-radius: 30px !important; border-top-right-radius: 30px !important;}
        
.MuiOutlinedInput-root {
  background: transparent;
}


        .MuiAutocomplete-popper { z-index: 9999 !important; }
      `}
    </style>
    <Modal
      show={openModal}
      onHide={() => { setOpenModal(false); setEditingItem(null); }}
      centered
      size="lg"
      dialogClassName="modal-full-rounded"
    >
      <div
        className="modal-header"
        style={{
          backgroundColor: "#0093D1",
      
        }}
      >
        <h3 className="modal-title text-white fw-bold mt-3 mb-3 mr">{editingItem ? "EDIT MECHANIC" : "NEW MECHANIC"}</h3>
        <button className="btn-close btn-close-white" onClick={() => { setOpenModal(false); setEditingItem(null); }} />
      </div>

      <Modal.Body style={{ backgroundColor: "#FFFFFF", padding: "30px" }}>
        <div className="d-flex justify-content-center rounded">
          <div style={{ width: "100%", maxWidth: "700px" }}>

            <div className="d-flex gap-4 mb-4">
              {editingItem ? (
                <div className="row w-100">
                    <div className="col-6 mb-3">
                        <label className="form-label" style={{ color: "#000", fontWeight: "500"}}>User</label>
                        <input type="text" className="form-control" style={inputStyle}
                            value={editingItem.ruleType === 'patient' ? editingItem.patientNric : editingItem.hcpName}
                            disabled
                        />
                    </div>
                    <div className="col-6 mb-3">
                        <label className="form-label" style={{ color: "#000", fontWeight: "500"}}>Status</label>
                        <div className="d-flex align-items-center" style={{ height: "52px" }}>
                            <div className="form-check form-switch mb-0">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    role="switch"
                                    id="statusSwitch"
                                    checked={isActive}
                                    onChange={e => setIsActive(e.target.checked)}
                                    style={{ cursor: "pointer" }}
                                />
                                <label className="form-check-label ms-2" htmlFor="statusSwitch"
                                    style={{ fontWeight: 600, color: isActive ? "#28A745" : "#FF1E1E" }}>
                                    {isActive ? "ACTIVE" : "INACTIVE"}
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
              <>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  id="ruleTypeHospital"
                  checked={ruleType === "hospital"}
                  onChange={() => { setRuleType("hospital"); setSelectedHcp(null); setPatientNric(""); setSelectedPatient(null); setErrors(prev => ({...prev, patientNric: ""})); }}
                />
                <label className="form-check-label" htmlFor="ruleTypeHospital" style={{ fontWeight: 500 }}>HCP</label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  id="ruleTypePatient"
                  checked={ruleType === "patient"}
                  onChange={() => { setRuleType("patient"); setSelectedHcp(null); setSelectedPatient(null); setPatientNric(""); setErrors(prev => ({...prev, hospitalName: ""})); }}
                />
                <label className="form-check-label" htmlFor="ruleTypePatient" style={{ fontWeight: 500 }}>Patient (NRIC)</label>
              </div>
              </>
            )}
            </div>
          

            <h5 style={{ fontWeight: 500, fontSize: "20px", marginBottom: "20px", color: "#333" }}>Basic Information</h5>

            <div className="row">
              {!editingItem && (
              <div className="col-6 mb-3">
                {ruleType === "hospital" ? (
                  <>
                    <label className="form-label" style={{ color: "#000", fontWeight: "500"}}>Hospital *</label>
                    <Autocomplete
                      options={hcpList}
                      getOptionLabel={option => option.hcpName}
                      value={selectedHcp}
                      onChange={(e, value) => {
                        setSelectedHcp(value);
                        setErrors(prev => ({...prev, hospitalName: ""}));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label= "Search Hospital "
                          placeholder="Search hospital..."
                          sx={{
                            '& .MuiOutlinedInput-root': { borderRadius: "8px", fontSize: "15px" },
                            '& .MuiOutlinedInput-notchedOutline': { borderWidth: "2px", borderColor: "#979797" },
                          }}
                        />
                      )}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      noOptionsText="No hospital found"
                    />
                    {errors.hospitalName && <small className="text-danger d-block mt-1">Hospital is required</small>}
                  </>
                ) : (
                  <>
                    <label className="form-label z-20" style={{ color: "#000", fontWeight: "500"}}>Patient NRIC *</label>
                    <Autocomplete
                      options={patientList}
                      getOptionLabel={option => option.nric}
                      value={selectedPatient}
                      onChange={(e, value) => {
                        setSelectedPatient(value);
                        setPatientNric(value?.nric || "");
                        setErrors(prev => ({...prev, patientNric: ""}));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Search Patient NRIC"
                          placeholder="Search patient NRIC..."
                          sx={{
                            '& .MuiOutlinedInput-root': { height: "52px", borderRadius: "8px", fontSize: "15px" },
                            '& .MuiOutlinedInput-notchedOutline': { borderWidth: "2px", borderColor: "#979797" },
                          }}
                        />
                      )}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      noOptionsText="No patient found"
                    />
                    {errors.patientNric && <small className="text-danger d-block mt-1">Patient NRIC is required</small>}
                  </>
                )}
              </div>
              )}

              <div className={`${editingItem ? 'col-12' : 'col-6'} mb-3`}>
                <label className="form-label" style={{ color: "#000", fontWeight: "500"}}>Brand *</label>
                <select
                  className="form-select"
                  style={inputStyle}
                  value={selectedBrandId}
                  onChange={e => { setSelectedBrandId(e.target.value); setErrors(prev => ({...prev, selectedBrandId: ""})); }}
                >
                  <option value="">Select Brand</option>
                  {brandList.map(b => (
                    <option key={b.id} value={b.id}>{b.medication} (Buy {b.threshold})</option>
                  ))}
                </select>
                {errors.selectedBrandId && <small className="text-danger d-block mt-1">{errors.selectedBrandId}</small>}
                {selectedBrandId && (() => {
                   const b = brandList.find(b => b.id === selectedBrandId);
                  return b ? <small className="text-muted d-block mt-1">Brand default: Buy {b.threshold}</small> : null;
                })()}
              </div>
            </div>

            <h5 style={{ fontWeight: 500, fontSize: "20px", marginBottom: "20px", color: "#333" }}>Duration</h5>

            <div className="row">
              <div className="col-6 mb-3">
                <label className="form-label" style={{ color: "#000", fontWeight: "500"}}>Start Date </label>
                <DatePicker
                  selected={startDate}
                  onChange={date => setStartDate(date)}
                  className="form-control"
                  style={{ ...inputStyle}}
                  dateFormat="dd/MM/yyyy"
              
                />
              </div>

              <div className="col-6 mb-3">
                <label className="form-label" style={{ color: "#000", fontWeight: "500" }}>End Date </label>
                <DatePicker
                  selected={endDate}
                  onChange={date => setEndDate(date)}
                  className="form-control"
                  style={{ ...inputStyle, width: "100%" }}
                  dateFormat="dd/MM/yyyy"
                  
                />
              </div>
            </div>

            <hr />

            <h5 style={{ fontWeight: 500, fontSize: "20px", marginBottom: "20px", marginTop: "20px", color: "#333" }}>Mechanic Rules</h5>

            <div className="row">
              <div className="col-6 mb-3">
                <label className="form-label" style={{ color: "#000" , fontWeight: "500"}}>Buy Quantity *</label>
                <input
                  type="number"
                  className="form-control"
                  style={inputStyle}
                  value={threshold}
                  onChange={e => { setThreshold(e.target.value); setErrors(prev => ({...prev, threshold: ""})); }}
                  
                />
                {errors.threshold && <small className="text-danger d-block mt-1">{errors.threshold}</small>}
              </div>

              <div className="col-6 mb-3">
                <label className="form-label" style={{ color: "#000" , fontWeight: "500"}}>Get Quantity *</label>
                <input
                  type="number"
                  className="form-control"
                  style={inputStyle}
                  value={redeemQuantity}
                  onChange={e => { setRedeemQuantity(e.target.value); setErrors(prev => ({ ...prev, redeemQuantity: ""})); }}
                  
                />
                {errors.redeemQuantity && <small className="text-danger d-block mt-1">{errors.redeemQuantity}</small>}
              </div>
            </div>

            {((ruleType === "hospital" ? selectedHcp : patientNric) || selectedBrandId || threshold || redeemQuantity) && (
              <div
                style={{
                  border: "2px solid #0093D1",
                  background: "#EAF7FC",
                  borderRadius: "12px",
                  padding: "24px",
                  marginTop: "30px"
                }}
              >
                <p style={{ fontSize: "14px", color: "#555", marginBottom: "20px" }}>
                Rule Preview</p>
               <p className="fw-bold" style={{ color: "#0093D1", fontSize: "18px" }}>
                   {ruleType === "hospital" ? `Hospital: ${selectedHcp?.hcpName || "Hospital"}` : `Patient NRIC: ${patientNric || "N/A"}`}
                
                </p>
                <p className="fw-bold" style={{ color: "#0093D1", fontSize: "18px" }}>
                  Brand Name: {brandList.find(b => b.id === selectedBrandId)?.medication || "N/A"}
                </p>
                <p className="fw-bold" style={{ color: "#0093D1", fontSize: "18px" }}>
                  Duration:{" "}
                  {startDate && endDate
                    ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
                    : "N/A"}
                </p>
                <p className="fw-bold" style={{ color: "#0093D1", fontSize: "18px" }}>
                  Buy {threshold || "XX"} Get {redeemQuantity || "XX"} FREE
                </p>
              </div>
            )}
            <hr />
          </div>
        </div>

      </Modal.Body>

      <Modal.Footer style={{ padding: "24px 32px", borderTop: "none", display: "flex", gap: "16px"}}>
        <button
          onClick={() => { setOpenModal(false); setEditingItem(null); }}
          style={{
            flex: 1,
            height: "56px",
            borderRadius: "12px",
            border: "1px solid #CFCFCF",
            backgroundColor: "#fff",
            fontWeight: 600
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={submitting}
          style={{
            flex: 1,
            height: "56px",
            borderRadius: "12px",
            border: "none",
            backgroundColor: "#0093D1",
            color: "#fff",
            fontWeight: 600
          }}
        >
          {submitting ? "Saving..." : editingItem ? "Update Mechanic" : "Create Mechanic"}
        </button>
      </Modal.Footer>
    </Modal>
    </>
  );
};
const mapStateToProps = state => ({
  token: state.admin.token,
  role: state.admin.role
});
export default connect(mapStateToProps)(MechanicsManager); 